import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { SecurityHeaders } from '@/components/SecurityHeaders';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const email = searchParams.get('email');
  const password = searchParams.get('password');
  const firstName = searchParams.get('firstName');
  const lastName = searchParams.get('lastName');
  const phone = searchParams.get('phone');
  const checkoutData = searchParams.get('data');

  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  const [verified, setVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!email || !password || !firstName || !lastName || !checkoutData) {
      toast.error("נתונים חסרים", {
        description: "לא נמצאו נתוני אימות נדרשים",
      });
      navigate('/checkout');
      return;
    }

    // If user is already logged in, redirect to checkout
    if (user) {
      navigate('/checkout');
      return;
    }

    // Auto-send verification code on page load
    sendVerificationCode();
  }, [email, password, firstName, lastName, phone, checkoutData, user]);

  // Countdown timer for resend button
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  const sendVerificationCode = async () => {
    if (!email) return;

    setIsSendingCode(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-verification-code', {
        body: {
          email,
          type: 'send'
        }
      });

      if (error) {
        console.error('Error sending verification code:', error);
        toast.error("שגיאה בשליחת קוד", {
          description: "לא ניתן לשלוח קוד אימות. נסה שוב מאוחר יותר",
        });
        return;
      }

      setCodeRequested(true);
      setCountdown(60); // 60 seconds countdown
      toast.success("קוד נשלח בהצלחה", {
        description: `קוד אימות נשלח אל ${email}`,
      });
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast.error("שגיאה בשליחת קוד", {
        description: "אירעה שגיאה בשליחת קוד האימות",
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  const verifyCode = async () => {
    if (!email || !verificationCode || !password || !firstName || !lastName) return;

    setIsLoading(true);
    try {
      // Trim and clean the verification code
      const cleanCode = verificationCode.trim().replace(/\s/g, '');

      const { data, error } = await supabase.functions.invoke('send-verification-code', {
        body: {
          email: email.trim(),
          code: cleanCode,
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone?.trim() || undefined,
          type: 'verify'
        }
      });

      if (error) {
        console.error('Error verifying code:', error);
        toast.error("קוד שגוי", {
          description: "הקוד שהזנת אינו נכון או שפג תוקפו. בדוק ונסה שוב",
        });
        return;
      }

      if (data.success) {
        setVerified(true);
        toast.success("חשבון נוצר בהצלחה!", {
          description: "החשבון שלך נוצר ואומת. מועבר לתשלום...",
        });

        // Sign in the user after successful account creation
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (signInError) {
          console.error('Error signing in after verification:', signInError);
          toast.error("שגיאה בהתחברות", {
            description: "החשבון נוצר אך אירעה שגיאה בהתחברות. התחבר ידנית",
          });
          navigate('/checkout');
          return;
        }

        // Redirect to checkout with verification flag
        setTimeout(() => {
          navigate(`/checkout?verified=true&data=${checkoutData}`);
        }, 2000);
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      toast.error("שגיאה באימות", {
        description: "אירעה שגיאה באימות הקוד",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    if (countdown > 0) return;
    sendVerificationCode();
  };

  const formatEmail = (email: string) => {
    const [local, domain] = email.split('@');
    if (local.length <= 3) return email;
    return `${local.substring(0, 2)}***@${domain}`;
  };

  if (verified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <SecurityHeaders />
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto">
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <CardTitle className="text-green-800">חשבון נוצר בהצלחה!</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-green-700 mb-4">
                  החשבון שלך נוצר ואומת בהצלחה. מועבר לתשלום...
                </p>
                <div className="flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <SecurityHeaders />
      <Navigation />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/checkout')}
            className="mb-6 hover:bg-muted/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            חזרה לקופה
          </Button>

          <Card className="shadow-lg border-border/50 bg-card/95 backdrop-blur-sm">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-xl text-foreground">אימות כתובת אימייל</CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                יצירת חשבון עבור {formatEmail(email || '')}
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Show user details for confirmation */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <h3 className="font-medium text-sm text-foreground">פרטי החשבון שיווצר:</h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div>שם: {firstName} {lastName}</div>
                  <div>אימייל: {formatEmail(email || '')}</div>
                  <div className="flex items-center gap-2">
                    <span>סיסמה:</span>
                    <div className="flex items-center gap-1">
                      <span className="font-mono">
                        {showPassword ? password : '••••••••'}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {!codeRequested ? (
                <div className="text-center">
                  <Button
                    onClick={sendVerificationCode}
                    disabled={isSendingCode}
                    className="w-full"
                  >
                    {isSendingCode ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        שולח קוד אימות...
                      </>
                    ) : 'שלח קוד אימות'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Alert>
                    <Mail className="h-4 w-4" />
                    <AlertDescription>
                      קוד אימות בן 6 ספרות נשלח לכתובת האימייל שלך.
                      הקוד בתוקף למשך 10 דקות.
                    </AlertDescription>
                  </Alert>

                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">
                      הזן קוד אימות
                    </label>
                    <Input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="text-center text-lg tracking-widest"
                      maxLength={6}
                      disabled={isLoading}
                    />
                  </div>

                  <Button
                    onClick={verifyCode}
                    disabled={isLoading || verificationCode.length !== 6}
                    className="w-full"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        יוצר חשבון...
                      </>
                    ) : 'צור חשבון ואמת'}
                  </Button>

                  <div className="text-center text-sm text-muted-foreground">
                    לא קיבלת קוד?{' '}
                    <Button
                      variant="link"
                      onClick={handleResendCode}
                      disabled={countdown > 0 || isSendingCode}
                      className="p-0 h-auto text-sm"
                    >
                      {countdown > 0 ? `שלח שוב בעוד ${countdown} שניות` : 'שלח שוב'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EmailVerification;